import {
  ComponentMultiStyleConfig,
  Flex,
  FormLabel,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { hasValue } from "../../../../lib/util/common-util";
import {
  CMButtonFormComment,
  CMButtonFormCommentProps,
  Comments,
} from "../../comment";
import { CMFormChangeBadge } from "../../badge";
import { FormRequiredLabel, FormRequiredLabelProps } from "../../label";
import { ChangeBadgeProperties } from "../../../../lib";
import { useCheckEqualityInDiffMode } from "../../../../hooks/use-check-equality-in-diff-mode";

export type CMFormControlHeaderProps<T extends {}> = {
  /**
   * ラベル。
   */
  label?: string;

  /**
   * コントロールの現在の値。
   * 差分モードの判定にのみ用いる。
   */
  value?: T | null;

  /**
   * 必須マークの設定。
   * この項目が設定されている場合は必須ラベルを表示する。
   */
  requiredLabelProps?: FormRequiredLabelProps;

  /**
   * 変更バッジのprops。
   * 値があれば、このコントロールは差分モード(差分があれば変更バッジとオレンジ枠を表示するモード)となる。
   * isJudgeByKeyがONの場合はchangedKeyFromの値を判定に使用する。
   */
  changeBadgeProperties?: ChangeBadgeProperties<T>;

  /**
   * 値を、表示値に変換する関数。
   */
  toDisplayValue?: (value: T) => string | Promise<string>;

  /**
   * 2つの値が同じかどうかを判定する関数。
   * 差分モード時に使用する。
   * 基本的には省略可で、省略した場合は以下のデフォルトを用いる。
   * - プリミティブ値の場合: 単純な ===
   * - 配列の場合: 順序を無視した、要素単位の差分比較
   * - その他の場合: lodashのisEqual
   */
  checkEqualityFn?: (v0: T, v1: T) => boolean | Promise<boolean>;

  /**
   * コメントボタンのprops。
   * これが存在すれば、コメントボタンを表示する。
   */
  commentButtonProps?: CMButtonFormCommentProps;

  /**
   * 修正依頼コメント・返信履歴のprops（履歴参照画面用）
   */
  commentsList?: Comments[];
} & ComponentStyleProps;

/**
 * フォームコントロールヘッダーを表示するコンポーネント
 * 必須マーク、変更有無バッジ、コメントボタンの表示を制御する。
 */
export const CMFormControlHeader = <T extends {}>({
  label,
  value = null,

  requiredLabelProps,

  changeBadgeProperties,
  toDisplayValue,
  checkEqualityFn,

  commentButtonProps,
  commentsList,

  sx,
  ...rest
}: CMFormControlHeaderProps<T>) => {
  const styles = useMultiStyleConfig("CMFormControlHeaderStyles");

  const isDiffMode = useMemo(
    () => hasValue(changeBadgeProperties),
    [changeBadgeProperties],
  );
  const _toDisplayValue = useCallback(
    (value: T) => {
      if (hasValue(toDisplayValue)) return toDisplayValue(value);

      // toDisplayValueが提供されていないなら、なるべく簡単に文字列変換して返す。
      if (typeof value === "string") return value;
      if (typeof value === "number") return value.toString(10);
      if (typeof value === "boolean") return value.toString();
      if (Array.isArray(value))
        return value.map((v) => v.toString()).join(", ");
      return JSON.stringify(value);
    },
    [toDisplayValue],
  );

  const { hasChangedInDiffMode } = useCheckEqualityInDiffMode(
    isDiffMode,
    changeBadgeProperties?.changedFrom,
    value,
    checkEqualityFn,
  );

  return (
    <Flex sx={{ ...styles.container, ...sx }} {...rest}>
      <FormLabel sx={styles.label}>
        <Text sx={styles.labelText}>{label}</Text>
      </FormLabel>

      {hasValue(requiredLabelProps) ? (
        <FormRequiredLabel sx={styles.requiredLabel} {...requiredLabelProps} />
      ) : null}

      {hasChangedInDiffMode && (
        <CMFormChangeBadge<T>
          {...changeBadgeProperties}
          toDisplayValue={_toDisplayValue}
          sx={styles.changeBadge}
        />
      )}
      {hasValue(commentButtonProps) && (
        <CMButtonFormComment
          sx={styles.commentButton}
          {...commentButtonProps}
          commentsList={commentsList}
        />
      )}
    </Flex>
  );
};

export const CMFormControlHeaderStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "label",
    "labelText",
    "changeBadge",
    "commentButton",
    "requiredLabel",
  ],
  baseStyle: {
    container: {
      display: "flex",
      flexFlow: "row nowrap",
      alignItems: "center",
    },
    label: {
      m: "0",
    },
    labelText: {
      fontSize: "xs",
      color: "gray.600",
    },
    changeBadge: {
      ml: "6px",
    },
    commentButton: {
      ml: "7px",
    },
    requiredLabel: {
      ml: "7px",
    },
  },
};
